// 可拖动列宽 Hook - Apple Finder 风格
import { useState, useCallback, useRef, useEffect } from 'react';

interface ColumnWidths {
  category: number;
  series: number;
  product: number;
}

interface UseColumnResizeReturn {
  columnWidths: ColumnWidths;
  isResizing: string | null;
  handleResizeStart: (column: string, e: React.MouseEvent) => void;
}

const MIN_WIDTH = 120;
const MAX_WIDTH = 500;

/**
 * 列宽拖拽调整 Hook
 * 支持像 Apple Finder 一样的列宽拖拽调整
 */
export function useColumnResize(initialWidths: ColumnWidths): UseColumnResizeReturn {
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(initialWidths);
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const resizingColumn = useRef<string | null>(null);

  // 处理拖拽开始
  const handleResizeStart = useCallback((column: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(column);
    resizingColumn.current = column;
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = columnWidths[column as keyof ColumnWidths];
    
    // 添加拖拽中的 cursor 样式
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  // 全局事件监听：mousemove 和 mouseup
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizingColumn.current) return;
      
      const delta = e.clientX - resizeStartX.current;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, resizeStartWidth.current + delta));
      
      setColumnWidths(prev => ({
        ...prev,
        [resizingColumn.current!]: newWidth,
      }));
    };

    const handleMouseUp = () => {
      setIsResizing(null);
      resizingColumn.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    // 绑定到 document 以捕获拖拽到列外的情况
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  return {
    columnWidths,
    isResizing,
    handleResizeStart,
  };
}
